<template>
    <div class="h-100 w-100">
        <div ref="mapboxMap" class="mapbox-map w-100 h-100"></div>
        <template v-if="isReady">
            <slot :map="map" />
        </template>
    </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import extend from 'turf-extent'

export default {
  name: 'MapBox',
  props: ['draw', 'poly', 'sources', 'reDraw', 'center', 'icon'],
  data () {
    return {
      map: null,
      isReady: false,
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      mapStyle: 'mapbox://styles/human018/ckgtzfn5s0bwo19n2bevemdm9',
      mapCenter: this.center ? this.center : [116.143492, -34.251815],
      minZoom: {
        type: Number,
        default: 0
      },
      maxZoom: {
        type: Number,
        default: 22
      },
      Draw: null,
      pulsingDot: null,
      pulsingDotData: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: this.mapCenter
            }
          }
        ]
      },
      handleInterval: null
    }
  },
  mounted () {
    this.initializeMap()
    if (this.icon && this.icon === 'dot') {
      this.handleInterval = setInterval(() => {
        this.map.triggerRepaint()
        this.pulsingDotData.features[0].geometry.coordinates = this.center
        if (this.map && this.map.getSource('points')) {
          this.map.getSource('points').setData(this.pulsingDotData)
          this.map.panTo(this.center)
        }
      }, 100)
    }
  },
  beforeDestroy () {
    clearInterval(this.handleInterval)
    if (this.map) {
      this.map.remove()
    }
  },
  watch: {
    sources () {
      if (this.map) {
        this.resetMap()
        this.addBoundaries()
      }
    },
    reDraw () {
      if (this.poly) {
        const feature = {
          type: 'Polygon',
          coordinates: [
            this.poly
          ]
        }

        this.Draw.deleteAll().add(feature)
      }
    }
  },
  methods: {
    resetMap () {
      if (this.map) {
        if (this.map.getLayer('map-boundaries')) this.map.removeLayer('map-boundaries')
        if (this.map.getLayer('map-labels')) this.map.removeLayer('map-labels')
        if (this.map.getSource('sources')) this.map.removeSource('sources')
      }
    },
    polygonObject (source) {
      return {
        type: 'Feature',
        properties: {
          description: source.label || 'shell'
        },
        geometry: {
          type: 'Polygon',
          coordinates: [source.boundary]
        }
      }
    },
    initializeMap () {
      const mapContainer = this.$refs.mapboxMap
      mapboxgl.accessToken = this.accessToken

      this.map = new mapboxgl.Map({
        container: mapContainer,
        style: this.mapStyle,
        center: this.mapCenter,
        zoom: 15
      })

      if (this.icon && this.icon === 'dot') {
        this.addPulsingDot()
      }

      this.map.on('styledata', () => {
        this.isReady = true
      })

      this.map.on('load', () => {
        if (this.icon && this.icon === 'dot') {
          this.map.addImage('pulsing-dot', this.pulsingDot, { pixelRatio: 2 })
          this.map.addSource('points', {
            type: 'geojson',
            data: this.pulsingDotData
          })
          this.map.addLayer({
            id: 'points',
            type: 'symbol',
            source: 'points',
            layout: {
              'icon-image': 'pulsing-dot'
            }
          })
        }
        this.addBoundaries()
      })

      this.map.on('mouseup', (e) => {
        this.$emit('mousePosition', e.lngLat.wrap())
      })

      this.determineDraw()
      this.determineSearch()

      if (this.poly) {
        const shell = this.polygonObject({ boundary: this.poly })
        const bounds = extend(shell)
        this.map.fitBounds(bounds, {
          padding: { top: 25, bottom: 25, left: 15, right: 5 }
        })
      }
    },
    updateArea (e) {
      this.$emit('updatePoly', e.features[0].geometry.coordinates[0])
    },
    determineDraw () {
      if (this.draw) {
        this.Draw = new MapboxDraw({
          displayControlsDefault: false,
          controls: {
            polygon: true,
            trash: true
          }
        })

        this.map.addControl(this.Draw, 'top-left')

        if (this.poly) {
          const feature = {
            type: 'Polygon',
            coordinates: [
              this.poly
            ]
          }

          this.Draw.add(feature)
        }

        this.map.on('draw.create', this.updateArea)
        this.map.on('draw.delete', this.updateArea)
        this.map.on('draw.update', this.updateArea)
      }
    },
    determineSearch () {
      if (this.draw) {
        this.map.addControl(
          new MapboxGeocoder({
            accessToken: this.accessToken,
            mapboxgl: mapboxgl
          })
        )
      }
    },
    addBoundaries () {
      if (this.sources && this.sources.length > 0) {
        const sources = {
          type: 'FeatureCollection',
          features: []
        }
        this.sources.forEach(source => {
          sources.features.push(this.polygonObject(source))
        })

        this.map.addSource('sources', {
          type: 'geojson',
          data: sources
        })
        this.map.addLayer({
          id: 'map-boundaries',
          type: 'fill',
          source: 'sources',
          layout: {},
          paint: {
            'fill-color': '#088',
            'fill-opacity': 0.8
          }
        })
        this.map.addLayer({
          id: 'map-labels',
          type: 'symbol',
          source: 'sources',
          layout: {
            'text-field': ['get', 'description'],
            'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
            'text-radial-offset': 0.5,
            'text-justify': 'auto',
            'icon-image': ['concat', ['get', 'icon'], '-15']
          }
        })

        const bounds = extend(sources)
        this.map.fitBounds(bounds, {
          padding: { top: 25, bottom: 25, left: 15, right: 5 }
        })
      }
    },
    addPulsingDot () {
      const size = 200
      this.pulsingDot = {
        width: size,
        height: size,
        data: new Uint8Array(size * size * 4),

        // get rendering context for the map canvas when layer is added to the map
        onAdd: function () {
          const canvas = document.createElement('canvas')
          canvas.width = this.width
          canvas.height = this.height
          this.context = canvas.getContext('2d')
        },

        // called once before every frame where the icon will be used
        render: function () {
          const duration = 1000
          const t = (performance.now() % duration) / duration

          const radius = (size / 2) * 0.3
          const outerRadius = (size / 2) * 0.7 * t + radius
          const context = this.context

          // draw outer circle
          context.clearRect(0, 0, this.width, this.height)
          context.beginPath()
          context.arc(
            this.width / 2,
            this.height / 2,
            outerRadius,
            0,
            Math.PI * 2
          )
          context.fillStyle = 'rgba(71, 169, 66,' + (1 - t) + ')'
          context.fill()

          // draw inner circle
          context.beginPath()
          context.arc(
            this.width / 2,
            this.height / 2,
            radius,
            0,
            Math.PI * 2
          )
          context.fillStyle = 'rgb(71,169,66)'
          context.strokeStyle = 'white'
          context.lineWidth = 2 + 4 * (1 - t)
          context.fill()
          context.stroke()

          // update this image's data with data from the canvas
          this.data = context.getImageData(
            0,
            0,
            this.width,
            this.height
          ).data

          // return `true` to let the map know that the image was updated
          return true
        }
      }
    }
  }
}
</script>
